import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UserRoleEnum } from 'src/app/common/services/auth/enums/user-role.enum';
import { StorageService } from 'src/app/common/services/storage/storage.service';
import { StatusEnum } from 'src/app/common/utils/status-enum.models';
import { ButtonColorEnum } from 'src/app/shared/buttons/enums/button-color.enum';
import { InputTypeEnum } from 'src/app/shared/input/enums/input-type.enum';
import { MaskTypeEnum } from 'src/app/shared/input/enums/mask-type.enum';
import {
  CreateUserRequest,
  GetUserResponse,
  GetUserRequest,
} from '../../service/models/user.service.model';
import { UserService } from '../../service/user.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-user-new',
  templateUrl: './user-new.component.html',
  styleUrls: ['./user-new.component.scss'],
})
export class UserNewComponent implements OnInit {
  isLoading: boolean = false;
  error: boolean = false;
  selectSupervisorEnabled: boolean = true;
  roleOptions: string[] = [];
  activeOptions: string[] = ['Ativo', 'Inativo'];
  isSuperAdmin: boolean = false;

  supervisors: string[] = [];
  originalSupervisors: GetUserResponse[] = [];
  loadingSupervisors: boolean = false;
  selectedSupervisors: string[] = [];
  loadingSearchSupervisors: boolean = false;
  totalItemsSupervisors: number = 0;
  loadingMoreSupervisors: boolean = false;

  supervisorsFilter: GetUserRequest = {
    pageSize: 10,
    pageNumber: 1,
    profiles: ['SUPERVISOR'],
    status: [StatusEnum.ATIVO],
    sortAscending: true,
    sortBy: 'Name',
  } as GetUserRequest;

  form = this.formBuilder.group({
    name: ['', Validators.required],
    email: ['', Validators.required],
    functional: ['', Validators.required],
    supervisor: ['', Validators.required],
    active: ['', Validators.required],
    perfil: ['', Validators.required],
    recorderId: [null],
  });

  ButtonColorEnum = ButtonColorEnum;
  MaskTypeEnum = MaskTypeEnum;
  InputTypeEnum = InputTypeEnum;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly userService: UserService,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.loadingSupervisors = true;

    Object.keys(this.form.controls).forEach((key) => {
      const control =
        this.form.controls[key as keyof typeof this.form.controls];
      if (control.status == 'INVALID') control.markAsDirty();
    });

    this.form.controls.perfil.valueChanges.subscribe(
      (selectedValue: string | null) => {
        if (selectedValue === 'SUPERVISOR') {
          this.selectSupervisorEnabled = false;
          this.form.get('supervisor')?.clearValidators();
          this.form.get('supervisor')?.updateValueAndValidity();
          this.form.get('email')?.setValidators([Validators.required]);
          this.form.get('email')?.updateValueAndValidity();
        } else if (selectedValue == 'ATENDENTE') {
          this.selectSupervisorEnabled = true;
          this.form.get('supervisor')?.setValidators([Validators.required]);
          this.form.get('supervisor')?.updateValueAndValidity();
          this.form.get('email')?.clearValidators();
          this.form.get('email')?.updateValueAndValidity();
        } else {
          this.selectSupervisorEnabled = true;
          this.form.get('supervisor')?.setValidators([Validators.required]);
          this.form.get('supervisor')?.updateValueAndValidity();
          this.form.get('email')?.setValidators([Validators.required]);
          this.form.get('email')?.updateValueAndValidity();
        }
      }
    );

    this.getSupervisors(null);
    this.populateRoleOptions();
  }

  populateRoleOptions() {
    this.userService.getRoles().subscribe({
      next: (roles) => {
        this.roleOptions = roles;

        if (this.storageService.getRole() !== UserRoleEnum.supervisor)
          this.roleOptions = this.roleOptions.filter(
            (role) => role !== 'ATENDENTE'
          );
      },
    });
  }

  getSupervisors(name: string | null) {
    this.supervisorsFilter.pageNumber = 1;

    if (name) {
      this.supervisorsFilter.name = name;
      this.loadingSearchSupervisors = true;
    } else {
      this.loadingSupervisors = true;
      this.supervisorsFilter.name = null;
    }

    this.userService.get(this.supervisorsFilter).subscribe({
      next: (data) => {
        this.originalSupervisors = data.items;
        this.supervisors = data.items.map(
          (supervisor) => supervisor.name + ' | ' + supervisor.functional
        );
        this.totalItemsSupervisors = data.totalItems;
        this.loadingSupervisors = false;
        this.loadingSearchSupervisors = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os supervisores.');
        this.loadingSupervisors = false;
        this.loadingSearchSupervisors = false;
      },
    });
  }

  getMoreSupervisors() {
    if (this.totalItemsSupervisors != this.supervisors.length) {
      this.loadingMoreSupervisors = true;
      if (this.supervisorsFilter.pageNumber)
        this.supervisorsFilter.pageNumber += 1;

      this.userService.get(this.supervisorsFilter).subscribe({
        next: (data) => {
          this.originalSupervisors = this.originalSupervisors.concat(
            data.items
          );
          this.supervisors = this.supervisors.concat(
            data.items.map(
              (supervisor) => supervisor.name + ' | ' + supervisor.functional
            )
          );
          this.totalItemsSupervisors = data.totalItems;
          this.loadingMoreSupervisors = false;
        },
        error: () => {
          this.toastrService.error(
            'Não foi possível carregar os supervisores.'
          );
          this.loadingMoreSupervisors = false;
        },
      });
    }
  }

  getSupervisorIdFromText(text: string): string | null {
    if (!text || text.trim() === '') return null;

    const [functionalString, name] = text.split('|').map((part) => part.trim());
    const supervisor = this.originalSupervisors.find(
      (attendant) =>
        attendant.functional.toString() === functionalString &&
        attendant.name.trim() === name.trim()
    );

    return supervisor ? supervisor.id : null;
  }

  handleNavigateBack() {
    this.form.reset();
    this.router.navigate(['/user/']);
  }

  handleCreateUser() {
    if (!this.form.valid || this.isLoading) return;

    this.isLoading = true;
    this.error = false;

    const request = {
      name: this.form.controls.name.value ?? '',
      email: this.form.controls.email.value ?? '',
      functional: Number(this.form.controls.functional.value) || '',
      supervisorId:
        this.getSupervisorIdFromText(
          this.form.controls.supervisor.value ?? ''
        ) ?? null,
      roleName: this.form.controls.perfil.value ?? '',
      active: this.form.controls.active.value === 'Ativo',
      recorderId: this.form.controls.recorderId.value ?? null,
    } as CreateUserRequest;

    if (request.roleName == 'ATENDENTE') {
      request.email = null;
    }

    if (request.roleName == 'SUPERVISOR') {
      request.supervisorId = null;
    }

    this.userService.createUser(request).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastrService.success('Usuário cadastrado com sucesso!');
        this.form.reset();
        this.router.navigate(['/user']);
      },
      error: (e) => {
        this.isLoading = false;
        this.error = true;
        this.toastrService.error(`Falha ao cadastrar usuário: ${e.error}`);
      },
    });
  }
}
